import React, { FC, Fragment, useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import { difference, isNull } from 'lodash';
import { Field, FieldArrayRenderProps, FieldProps, useFormikContext } from 'formik';
import { Card, CardContent, Divider, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import linkifyIt from 'linkify-it';
import tlds from 'tlds';

import { ContentInput } from 'api';
import { PostAction, UserGroup } from 'types/app';
import { SelectedNetworksContext } from 'providers';
import { isAdminUser, isContributorUser } from 'helpers';
import { getNetwork, hasVideo } from 'features/content/helpers';

import {
  Editor,
  EditorFormat,
  EditorModes,
  PhotoVideoUpload,
  UserAvatar,
  ValidatedSwitch,
} from 'components';

import Preview from './Preview';
import { RemoveTypes } from '../types';
import {
  AdminCustomization,
  Header,
  MarkImportant,
  PinPost,
  PostButton,
  EngagePost,
} from './EditorPartials';
import { PostSchema } from '../validations';
import { useCurrentUser } from 'hooks';

interface IProps {
  action: PostAction;
  working: boolean;
  onClose: () => void;
  groups: UserGroup[];
  canModerate: boolean;
  onGroupToggle: (group: UserGroup, arrayHelper: FieldArrayRenderProps) => void;
  selectedGroup: UserGroup;
}

const linkify = linkifyIt({ fuzzyEmail: false });
const AVATAR_COLUMN_WIDTH = 56;

linkify.tlds(tlds);

const useStyles = makeStyles(theme => ({
  root: {
    marginBottom: theme.spacing(3),
  },
  editorContainer: {
    display: 'flex',
    paddingBottom: theme.spacing(1),
  },
  editorContainerLeft: {
    width: AVATAR_COLUMN_WIDTH,
  },
  editorContainerRight: {
    width: `calc(100% - ${AVATAR_COLUMN_WIDTH}px)`,
  },
  editor: {
    marginTop: theme.spacing(1),
  },
  contentActions: {
    paddingTop: theme.spacing(0.5),
    paddingBottom: 0,
  },
  contentOptions: {
    paddingBottom: 0,
  },
  dividerHeader: {
    marginBottom: 12,
  },
  dividerActions: {
    marginTop: theme.spacing(1),
  },
  dividerButton: {
    marginBottom: theme.spacing(2),
  },
  spacer: {
    margin: theme.spacing(0.5, 0),
  },
}));

const CreatorEditor: FC<IProps> = ({
  onClose,
  canModerate,
  action,
  groups,
  onGroupToggle,
  working,
  selectedGroup,
}) => {
  const classes = useStyles();
  const user = useCurrentUser();
  const { values, setFieldValue, resetForm, isValid } = useFormikContext<ContentInput>();
  const [removedUrls, setRemovedUrls] = useState([]);

  const [network, setNetwork] = useState(null);
  useEffect(() => {
    if (values.is_engageable) {
      setNetwork(getNetwork(values));
    }
  }, [values.is_engageable, values.content_object.url]);

  function getModelUrl() {
    return values.content_object.url;
  }

  function handleClose() {
    resetForm();
    onClose();
  }

  function handleLinkCheck(value: string) {
    if (isNull(value)) {
      return;
    }

    const currentModelUrl = getModelUrl();
    const editorLinkMatches = linkify.match(value) || [];
    const editorUrls = editorLinkMatches.map(match => match.url);
    const availablePreviewUrls = difference(editorUrls, removedUrls);

    if (!currentModelUrl && availablePreviewUrls.length) {
      setFieldValue('content_object.url', availablePreviewUrls[0]);
    }
  }

  const handleRemove = (type: RemoveTypes) => () => {
    const { content_object } = values;

    if (type === RemoveTypes.Url) {
      setFieldValue('content_object.url', null);
      setFieldValue('is_engageable', false);
      setFieldValue('is_shareable', false);

      removedUrls.push(content_object.url);
      setNetwork(null);
      setRemovedUrls(removedUrls);
    }

    if (type === RemoveTypes.Video) {
      setFieldValue('content_object.media', null);
    }

    if (type === RemoveTypes.Photo) {
      setFieldValue('content_object.photos', null);
    }
  };

  function renderEditor() {
    return (
      <div className={classes.editorContainer}>
        <div className={classes.editorContainerLeft}>
          <UserAvatar size={40} user={user} />
        </div>
        <div className={classes.editorContainerRight}>
          <Field name="content_object.body">
            {({ field }: FieldProps<string>) => (
              <Editor
                disableMentions
                disableToolbar
                focusOnMount
                format={EditorFormat.text}
                text={field.value}
                mode={EditorModes.create}
                className={classes.editor}
                placeholder={<Trans i18nKey="posting:prompt" />}
                onEnter={handleLinkCheck}
                onSpace={handleLinkCheck}
                onPaste={handleLinkCheck}
                onChange={(value: string) => {
                  setFieldValue('content_object.body', value);
                }}
              />
            )}
          </Field>
        </div>
      </div>
    );
  }

  function renderOptions() {
    const isVideoPost = hasVideo(values);

    return (
      <div className={classes.spacer}>
        <Grid container alignItems="center" justify="space-between">
          <Grid item>
            <PhotoVideoUpload />
          </Grid>
          {!isVideoPost && (
            <Grid item>
              <ValidatedSwitch
                name="is_shareable"
                color="secondary"
                label={<Trans i18nKey="posting:shareable" />}
              />
            </Grid>
          )}
        </Grid>
      </div>
    );
  }

  function renderAdminOptions() {
    if (!isAdminUser(user)) {
      return null;
    }

    return (
      <Fragment>
        {!!network && (
          <Fragment>
            <Divider />
            <div className={classes.spacer}>
              <EngagePost network={network} />
            </div>
          </Fragment>
        )}
        <Divider />
        <div className={classes.spacer}>
          <PinPost />
        </div>
        <Divider />
        <div className={classes.spacer}>
          <MarkImportant />
        </div>
      </Fragment>
    );
  }

  function renderAdminCustomization() {
    const isVideoPost = hasVideo(values);

    if (isVideoPost) {
      return null;
    }

    if (!isAdminUser(user) && !isContributorUser(user) && !canModerate) {
      return null;
    }

    return (
      <Fragment>
        <Divider />
        <CardContent className={classes.contentOptions}>
          <AdminCustomization allowUtm={isAdminUser(user)} />
        </CardContent>
      </Fragment>
    );
  }

  const isDoubleValidated = isValid && PostSchema.isValidSync(values);

  return (
    <Card className={classes.root}>
      <Header action={action} onClose={handleClose} groups={groups} onGroupToggle={onGroupToggle} />
      <Divider className={classes.dividerHeader} />
      <CardContent className={classes.contentActions}>
        {renderEditor()}
        <Preview selectedGroup={selectedGroup} working={working} onRemove={handleRemove} />
      </CardContent>
      <Divider className={classes.dividerActions} />
      <CardContent className={classes.contentOptions}>
        {renderOptions()}
        {renderAdminOptions()}
      </CardContent>
      {renderAdminCustomization()}
      <Divider className={classes.dividerButton} />
      <CardContent>
        <SelectedNetworksContext.Consumer>
          {({ selectedNetworks }) => {
            const createKey = !!selectedNetworks.length ? 'common:next' : 'common:share';
            const saveKey = action === PostAction.Create ? createKey : 'common:save';

            return <PostButton i18nKey={saveKey} working={working} disabled={!isDoubleValidated} />;
          }}
        </SelectedNetworksContext.Consumer>
      </CardContent>
    </Card>
  );
};

export default CreatorEditor;
